import { UzFormField, UzInput } from '@utazzo/ui';
import { Plus, Trash2 } from 'lucide-react';
import React from 'react';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';

import type { CreatePackageRequest, PackagePaymentTerm } from '@utazzo/shared';

const emptyRow = (): PackagePaymentTerm => ({ label: '', value: '' });

interface PaymentTermsEditorProps {
  form: UseFormReturn<CreatePackageRequest>;
  disabled?: boolean;
}

/** Key/value editor for payment schedule milestones on the public package page. */
export const PaymentTermsEditor = ({
  form,
  disabled,
}: PaymentTermsEditorProps): React.JSX.Element => {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'paymentTerms',
  });

  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">
        Each row is one milestone on the public page, e.g. &quot;At the time of Booking&quot; →
        &quot;15,000 PP&quot;.
      </p>

      {fields.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-4 text-center text-xs text-zinc-500">
          No payment terms yet.
        </p>
      ) : (
        <div className="space-y-2">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="grid gap-3 rounded-md border border-zinc-200 bg-zinc-50/60 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
            >
              <UzFormField name={`paymentTerms.${index}.label`}>
                {({ field: f, error }) => (
                  <UzInput
                    {...f}
                    value={(f.value as string) ?? ''}
                    label={index === 0 ? 'Label' : undefined}
                    placeholder="At the time of Booking"
                    error={error}
                    disabled={disabled}
                  />
                )}
              </UzFormField>
              <UzFormField name={`paymentTerms.${index}.value`}>
                {({ field: f, error }) => (
                  <UzInput
                    {...f}
                    value={(f.value as string) ?? ''}
                    label={index === 0 ? 'Value' : undefined}
                    placeholder="15,000 PP"
                    error={error}
                    disabled={disabled}
                  />
                )}
              </UzFormField>
              <div className={index === 0 ? 'pt-6' : ''}>
                <button
                  type="button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-red-600 disabled:opacity-40"
                  aria-label="Remove payment term"
                  disabled={disabled}
                  onClick={() => remove(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!disabled && (
        <button
          type="button"
          onClick={() => append(emptyRow())}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-zinc-300 py-2 text-xs font-medium text-zinc-500 hover:border-zinc-400 hover:text-zinc-700"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add payment term
        </button>
      )}
    </div>
  );
};
